import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { isUsageUnknown } from '@/hooks/useConversationCost';
import { useHuntCost } from '@/hooks/useHuntCost';
import { useMy } from '@/hooks/useMy';
import { cn } from '@/lib/utils';
import { ChatPane } from '@/sections/insights/hunts/panes/ChatPane';
import { LogPane } from '@/sections/insights/hunts/panes/LogPane';
import { MemoryPane } from '@/sections/insights/hunts/panes/MemoryPane';
import {
  useHuntConversations,
  useUpdateHuntStatus,
} from '@/sections/insights/hunts/useHunts';
import type { GraphQuery, Hunt } from '@/types';
import { RiskPriority, RiskSeverity } from '@/types';
import { formatDate } from '@/utils/date.util';
import { formatCost } from '@/utils/formatCurrency.util';
import { getRiskStatusLabel } from '@/utils/riskStatus.util';

// Lazy-loaded to avoid pulling the heavy Vulnerabilities module into the
// import graph for tests that mock @/utils/date.util incompletely.
const FindingsPane = lazy(() =>
  import('@/sections/insights/hunts/panes/FindingsPane').then(m => ({
    default: m.FindingsPane,
  }))
);

const WorkflowPane = lazy(() =>
  import('@/sections/insights/hunts/panes/WorkflowPane').then(m => ({
    default: m.WorkflowPane,
  }))
);

const AttackPathsPane = lazy(() =>
  import('@/sections/insights/hunts/panes/AttackPathsPane').then(m => ({
    default: m.AttackPathsPane,
  }))
);

interface AutoStatusViewProps {
  hunt: Hunt;
}

// eslint-disable-next-line react-refresh/only-export-components
export function computeTimeRemaining(expiresAt: string, now: number): string {
  const expires = new Date(expiresAt).getTime();
  if (isNaN(expires)) return '—';
  const diff = expires - now;
  if (diff <= 0) return 'Expired';
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

const SEVERITY_TEXT_COLOR: Record<string, string> = {
  [RiskSeverity.Critical]: 'text-chariot-stroke-critical',
  [RiskSeverity.High]: 'text-chariot-stroke-high',
  [RiskSeverity.Medium]: 'text-chariot-stroke-medium',
  [RiskSeverity.Low]: 'text-chariot-stroke-low',
  [RiskSeverity.Info]: 'text-chariot-stroke-info',
  [RiskSeverity.Exposure]: 'text-chariot-text-secondary',
};

function getHighestSeverity(
  risks: Array<{ status: string }>
): RiskSeverity | null {
  if (risks.length === 0) return null;
  let highest: RiskSeverity | null = null;
  let highestPriority = Infinity;
  for (const risk of risks) {
    const { severity } = getRiskStatusLabel(risk.status);
    if (severity && RiskPriority[severity] < highestPriority) {
      highestPriority = RiskPriority[severity];
      highest = severity;
    }
  }
  return highest;
}

type TabId =
  | 'agents'
  | 'log'
  | 'findings'
  | 'attack-paths'
  | 'memory'
  | 'chat'
  | 'workflow';

const TABS: { id: TabId; label: string }[] = [
  { id: 'findings', label: 'Vulnerabilities' },
  { id: 'attack-paths', label: 'Attack Paths' },
  { id: 'workflow', label: 'Workflow' },
  { id: 'agents', label: 'Agents' },
  { id: 'log', label: 'Log' },
  { id: 'memory', label: 'Memory' },
  { id: 'chat', label: 'Chat' },
];

export function AutoStatusView({ hunt }: AutoStatusViewProps) {
  const { data: conversations = [] } = useHuntConversations(hunt.uuid);
  const { mutate: updateStatus, isPending: statusPending } =
    useUpdateHuntStatus();

  const huntRiskQuery: GraphQuery = useMemo(
    () => ({
      node: {
        labels: ['Risk'],
        relationships: [
          {
            label: ['REPORTED_BY'],
            target: {
              labels: ['Hunt'],
              filters: [{ field: 'key', operator: '=', value: hunt.key }],
            },
          },
        ],
      },
    }),
    [hunt.key]
  );
  const { data: huntRisks = [] } = useMy({
    resource: 'risk',
    query: huntRiskQuery,
  });
  const highestSeverity = getHighestSeverity(huntRisks);
  const vulnColor = highestSeverity
    ? (SEVERITY_TEXT_COLOR[highestSeverity] ?? 'text-chariot-brand-primary')
    : 'text-chariot-brand-primary';

  const [now, setNow] = useState(() => Date.now());
  const [activeTab, setActiveTab] = useState<TabId>('findings');
  const [chatInitialConvId, setChatInitialConvId] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (hunt.status !== 'active') return;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [hunt.status]);

  const timeRemaining = computeTimeRemaining(hunt.expiresAt, now);

  // Hunt cost is visible to anyone who can view hunts (Praetorian OR
  // customers with the enable_ai_automation flag). The query is always
  // enabled here since the parent only renders this component when hunt
  // access is already granted.
  const {
    data: costStatus,
    isLoading: costLoading,
    isError: costError,
    invalidate: invalidateCost,
  } = useHuntCost(hunt.uuid, { enabled: true });

  // F1: the hunt cost query has a 30s staleTime and never self-refetches. When
  // a hunt reaches a terminal state (completed/expired/stopped) the final spend
  // is settled, so invalidate once on the transition INTO a terminal status to
  // pull the final total. Edge-triggered via a ref so it fires exactly once per
  // transition, not on every render. Only fires when the query is enabled
  // (Praetorian); a no-op invalidate for a disabled query is harmless.
  const prevStatusRef = useRef(hunt.status);
  useEffect(() => {
    const TERMINAL = ['completed', 'expired', 'stopped'];
    const wasTerminal = TERMINAL.includes(prevStatusRef.current);
    const isTerminal = TERMINAL.includes(hunt.status);
    if (!wasTerminal && isTerminal) {
      invalidateCost();
    }
    prevStatusRef.current = hunt.status;
  }, [hunt.status, invalidateCost]);

  // USD-contract guard shared with CostChip via `formatCost`: USD formats
  // `$X.XX` (byte-identical to before); a non-USD code yields null → `—`.
  // `costUnknown` → the hunt has no recorded usage (old / never-run): show
  // `—`, not a misleading `$0.00`.
  const costUnknown = isUsageUnknown(costStatus);
  const costFormatted =
    costStatus?.total?.cost == null
      ? null
      : formatCost(costStatus.total.cost, costStatus.currency);
  const costDisplay =
    costError || costUnknown || costFormatted == null ? '—' : costFormatted;

  return (
    <div className="flex h-full flex-col">
      {/* Header: prompt */}
      <div className="border-chariot-stroke border-b p-3">
        <p
          className="text-chariot-text-primary line-clamp-3 text-sm leading-normal"
          title={hunt.prompt}
        >
          {hunt.prompt}
        </p>
      </div>

      {/* Scope: only shown when the hunt was restricted to specific assets */}
      {hunt.scope && hunt.scope.length > 0 && (
        <div className="border-chariot-stroke border-b p-3">
          <div className="text-chariot-text-secondary mb-1.5 text-[9px] tracking-wider uppercase">
            Scope
          </div>
          <div className="flex flex-wrap gap-1.5">
            {hunt.scope.map(key => {
              // Asset keys are `#asset#<dns>#<identifier>`. Show the DNS
              // (clearer to read) alongside the identifier (e.g. the IP),
              // with the full label in the tooltip.
              const full = key.replace(/^#asset#/, '');
              const lastHash = full.lastIndexOf('#');
              const dns = lastHash === -1 ? full : full.slice(0, lastHash);
              const identifier =
                lastHash === -1 ? '' : full.slice(lastHash + 1);
              return (
                <span
                  key={key}
                  title={full}
                  className="bg-chariot-background-secondary inline-flex max-w-[260px] items-baseline gap-1 rounded px-1.5 py-0.5 font-mono text-[11px]"
                >
                  <span className="text-chariot-text-primary truncate">
                    {dns}
                  </span>
                  {identifier && identifier !== dns && (
                    <span className="text-chariot-text-secondary shrink-0">
                      {identifier}
                    </span>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Stat grid: 4 tiles for customers, +COST (5th) for Praetorian. */}
      <div className="border-chariot-stroke @container border-b p-3">
        <div className="grid grid-cols-2 gap-2 @sm:grid-cols-3 @md:grid-cols-5">
          {/* 1. Vulnerabilities (was Findings, now first) */}
          <button
            type="button"
            onClick={() => setActiveTab('findings')}
            className="bg-chariot-background-secondary hover:outline-chariot-brand-primary/25 cursor-pointer rounded-md p-2 text-center transition-all hover:outline hover:outline-1"
          >
            <div className={cn(vulnColor, 'text-lg leading-none font-bold')}>
              {hunt.findingsCount}
            </div>
            <div
              className={cn(
                vulnColor,
                'mt-1 text-[9px] tracking-wider uppercase opacity-80'
              )}
            >
              Vulns
            </div>
          </button>
          {/* 2. Iterations */}
          <div className="bg-chariot-background-secondary rounded-md p-2 text-center">
            <div className="text-chariot-text-primary text-lg leading-none font-bold">
              {hunt.iterationCount}
            </div>
            <div className="text-chariot-text-secondary mt-1 text-[9px] tracking-wider uppercase">
              Iterations
            </div>
          </div>
          {/* 3. Agents */}
          <button
            type="button"
            onClick={() => setActiveTab('agents')}
            className="bg-chariot-background-secondary hover:outline-chariot-brand-primary/25 cursor-pointer rounded-md p-2 text-center transition-all hover:outline hover:outline-1"
          >
            <div className="text-chariot-text-primary text-lg leading-none font-bold">
              {conversations.length}
            </div>
            <div className="text-chariot-text-secondary mt-1 text-[9px] tracking-wider uppercase">
              Agents
            </div>
          </button>
          {/* 4. Remaining */}
          <div className="bg-chariot-background-secondary rounded-md p-2 text-center">
            <div className="text-chariot-text-primary text-lg leading-none font-bold">
              {hunt.status === 'expired' ||
              hunt.status === 'completed' ||
              hunt.status === 'stopped'
                ? '—'
                : timeRemaining}
            </div>
            <div className="text-chariot-text-secondary mt-1 text-[9px] tracking-wider uppercase">
              Remaining
            </div>
          </div>
          {/* 5. AI Cost — Praetorian-only. Mirrors the "Remaining" tile
            (non-interactive). Renders the value directly in the number
            slot; never reuses CostChip (its null→nothing would blank the
            tile). States: loading `…`; error / unknown (no recorded usage)
            `—`; otherwise the projected total e.g. `$1.23`. */}
          <div className="bg-chariot-background-secondary rounded-md p-2 text-center">
            <div
              className="text-chariot-text-primary text-lg leading-none font-bold tabular-nums"
              title={costUnknown ? 'Cost not tracked for this hunt' : undefined}
            >
              {costLoading ? '…' : costDisplay}
            </div>
            <div className="text-chariot-text-secondary mt-1 text-[9px] tracking-wider uppercase">
              Cost
            </div>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-chariot-stroke flex border-b">
        {TABS.filter(
          tab => tab.id !== 'workflow' || !!hunt.currentWorkflowRunId
        ).map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'relative px-3.5 py-2.5 text-xs font-medium transition-colors',
              activeTab === tab.id
                ? 'text-chariot-text-primary'
                : 'text-chariot-text-secondary hover:text-chariot-text-primary'
            )}
          >
            {tab.label}
            {activeTab === tab.id && (
              <span className="bg-chariot-brand-primary absolute right-3.5 bottom-0 left-3.5 h-0.5 rounded-full" />
            )}
          </button>
        ))}
      </div>

      {/* Tab content (scrollable) */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* Agents tab */}
        {activeTab === 'agents' && (
          <div className="flex flex-col gap-1.5 p-4">
            {conversations.length === 0 ? (
              <p className="text-chariot-text-secondary py-4 text-center text-xs">
                No agents yet
              </p>
            ) : (
              [...conversations]
                .sort(
                  (a, b) =>
                    (new Date(b.created).getTime() || 0) -
                    (new Date(a.created).getTime() || 0)
                )
                .slice(0, 20)
                .map(conv => (
                  <button
                    key={conv.uuid}
                    type="button"
                    onClick={() => {
                      setChatInitialConvId(conv.uuid);
                      setActiveTab('chat');
                    }}
                    className="bg-chariot-background-secondary hover:bg-chariot-background-secondary/80 flex items-center gap-2 rounded-md px-2.5 py-2 text-left transition-colors"
                  >
                    <span className="bg-chariot-text-secondary size-1.5 flex-shrink-0 rounded-full" />
                    <div className="min-w-0 flex-1">
                      <div className="text-chariot-text-primary truncate text-xs font-medium">
                        {conv.title ??
                          conv.topic ??
                          `Iteration ${conv.uuid.slice(0, 8)}`}
                      </div>
                      <div className="text-chariot-text-secondary font-mono text-[10px]">
                        {formatDate(conv.created)}
                      </div>
                    </div>
                  </button>
                ))
            )}
          </div>
        )}

        {/* Log tab */}
        {activeTab === 'log' && <LogPane hunt={hunt} />}

        {/* Findings tab */}
        {activeTab === 'findings' && (
          <Suspense fallback={null}>
            <FindingsPane hunt={hunt} />
          </Suspense>
        )}

        {activeTab === 'attack-paths' && (
          <Suspense fallback={null}>
            <AttackPathsPane hunt={hunt} />
          </Suspense>
        )}

        {/* Workflow tab */}
        {activeTab === 'workflow' && (
          <Suspense fallback={null}>
            <WorkflowPane
              hunt={hunt}
              onViewConversation={convId => {
                setChatInitialConvId(convId);
                setActiveTab('chat');
              }}
            />
          </Suspense>
        )}

        {/* Memory tab */}
        {activeTab === 'memory' && <MemoryPane hunt={hunt} />}

        {/* Chat tab */}
        {activeTab === 'chat' && (
          <ChatPane
            hunt={hunt}
            key={chatInitialConvId ?? 'default'}
            initialConvId={chatInitialConvId ?? undefined}
          />
        )}

        {/* Last error (shown in all tabs) */}
        {hunt.lastError && (
          <div className="bg-error/10 border-error-light mx-4 mb-4 rounded-md border p-3">
            <p className="text-error text-xs font-medium">Last Error</p>
            <p className="text-error mt-1 text-xs opacity-80">
              {hunt.lastError}
            </p>
          </div>
        )}
      </div>

      {/* Controls footer (pinned) */}
      {(hunt.status === 'active' || hunt.status === 'paused') && (
        <div className="border-chariot-stroke flex flex-shrink-0 gap-2 border-t p-3">
          {hunt.status === 'active' && (
            <Button
              size="sm"
              variant="outline"
              disabled={statusPending}
              onClick={() =>
                updateStatus({ uuid: hunt.uuid, status: 'paused' })
              }
              className="flex-1"
            >
              Pause
            </Button>
          )}
          {hunt.status === 'paused' && (
            <Button
              size="sm"
              disabled={statusPending}
              onClick={() =>
                updateStatus({ uuid: hunt.uuid, status: 'active' })
              }
              className="flex-1"
            >
              Resume
            </Button>
          )}
          <Button
            size="sm"
            variant="destructive"
            disabled={statusPending}
            onClick={() => updateStatus({ uuid: hunt.uuid, status: 'stopped' })}
            className="flex-1"
          >
            Stop
          </Button>
        </div>
      )}
    </div>
  );
}
